import { useMemo, useState } from "react";
import { SOLANA_CHAIN_IDS } from "@/lib/chains";
import { isSolanaAddress } from "@/lib/utils/address";

/**
 * Validate Ethereum address format
 */
export function isValidEthereumAddress(address: string): boolean {
  if (!address) return true; // Empty is valid (optional field)

  // Check if it matches 0x followed by 40 hexadecimal characters
  const ethereumAddressRegex = /^0x[a-fA-F0-9]{40}$/;
  return ethereumAddressRegex.test(address);
}

/**
 * Validate Solana address format (base58, 32-44 chars)
 */
export function isValidSolanaAddress(address: string): boolean {
  if (!address) return true;
  return isSolanaAddress(address);
}

/**
 * Validate any blockchain address (EVM or Solana)
 */
export function isValidBlockchainAddress(address: string): boolean {
  if (!address) return true;
  return isValidEthereumAddress(address) || isValidSolanaAddress(address);
}

interface UseAddressValidationResult {
  isValid: boolean;
  isTouched: boolean;
  showError: boolean;
  setTouched: (touched: boolean) => void;
  validate: () => boolean;
}

/**
 * Validate address for a specific chain. When chainId is provided,
 * only addresses matching that chain family are accepted.
 */
export function isValidAddressForChain(address: string, chainId?: number): boolean {
  if (!address) return true;
  if (!chainId) return isValidBlockchainAddress(address);
  if (SOLANA_CHAIN_IDS.has(chainId)) return isValidSolanaAddress(address);
  return isValidEthereumAddress(address);
}

/**
 * Hook to manage address validation state
 */
export function useAddressValidation(address: string, chainId?: number): UseAddressValidationResult {
  const [isTouched, setTouched] = useState(false);

  const isValid = useMemo(() => isValidAddressForChain(address, chainId), [address, chainId]);

  const showError = isTouched && !!address && !isValid;

  const validate = () => {
    setTouched(true);
    return isValid;
  };

  return {
    isValid,
    isTouched,
    showError,
    setTouched,
    validate,
  };
}
